import { FC, useState, useEffect } from 'react';
import { IorganizationBusinessData } from '../../../../../types/bussines';
import NudosButton from '../../../../../components/NudosComponents/NudosButton/NudosButton';
import NudosTextInput from '../../../../../components/NudosComponents/NudosTextInput/NudosTextInput';
import NudosStaticAlertInComponent from '../../../../../components/NudosComponents/NudosStaticAlertInComponent/NudosStaticAlertInComponent';
import NudosConfirmActionModal from '../../../../../components/NudosComponents/NudosConfirmActionModal/NudosConfirmActionModal';
import CouponsList from '../../../../../components/CouponsList/CouponsList';
import {
  getOrganizationCoupons,
  createOrganizationCoupon,
  deleteOrganizationCoupon
} from '../../../../../services/coupons.service';
import {
  displayErrorNotification,
  displaySuccessNotification
} from '../../../../../utils/displayNudosStandardNotifications';
import { Button } from 'antd';
import PlusButton from '../../../../../assets/images/searchBar/plusButton';
import { IOrganizationCoupon, ICouponType } from '../../../../../types/coupons.types';
import UsedCoupons from './UsedCoupons/UsedCoupons';
import CouponsTabLoader from './CouponsTabLoader/CouponsTabLoader';
import './CouponsTab.scss';

interface ICouponsTab {
  organizationData?: IorganizationBusinessData;
}

/**
 * Componente que muestra la información de cupones de una organización
 * @param {Object} props - Propiedades del componente
 * @param {IorganizationBusinessData} [props.organizationData] - Datos de la organización
 * @returns {JSX.Element} - Componente de cupones
 */
const CouponsTab: FC<ICouponsTab> = ({ organizationData }: ICouponsTab) => {
  const [creatingCoupon, setCreatingCoupon] = useState<boolean>(false);
  const [newCouponValue, setNewCouponValue] = useState<number>();
  const [coupons, setCoupons] = useState<IOrganizationCoupon[]>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [couponIdToDelete, setCouponIdToDelete] = useState<number>();
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);

  const usedCoupons = coupons?.filter(coupon => !!coupon.usedAt) || [];

  const getAllCoupons = async () => {
    if (!organizationData?.id) return;
    setIsLoading(true);
    try {
      const organizationCoupons = await getOrganizationCoupons(organizationData.id);
      if (organizationCoupons) setCoupons(organizationCoupons);
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
      setIsInitialLoading(false);
    }
  };

  const handleCreateOrganizationCoupon = async () => {
    if (!organizationData?.id || !newCouponValue) return;
    setIsLoading(true);
    try {
      const newCoupon = await createOrganizationCoupon({
        organizationId: organizationData?.id,
        couponTypeId: ICouponType.GENERAL_COUPON,
        discountAmount: newCouponValue
      });
      if (!newCoupon) return;
      await getAllCoupons();
      setCreatingCoupon(false);
      setNewCouponValue(undefined);
      displaySuccessNotification({
        customJSXMessage: <>Cupón creado correctamente</>
      });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteCoupon = async (couponId: number) => {
    setCouponIdToDelete(couponId);
    setShowDeleteModal(true);
  };

  const confirmDeleteCoupon = async () => {
    if (!organizationData?.id || !couponIdToDelete) return;
    setIsLoading(true);
    try {
      await deleteOrganizationCoupon(couponIdToDelete);
      setCoupons(prevCoupons => prevCoupons?.filter(coupon => coupon.id !== couponIdToDelete));
      displaySuccessNotification({
        customJSXMessage: <>Cupón se elimino correctamente </>
      });
    } catch (error) {
      displayErrorNotification();
    } finally {
      setIsLoading(false);
      setShowDeleteModal(false);
      setCouponIdToDelete(undefined);
    }
  };

  const cancelCouponCreation = () => {
    setCreatingCoupon(false);
    setNewCouponValue(undefined);
  };

  useEffect(() => {
    if (organizationData?.id) getAllCoupons();
  }, [organizationData]);

  if (isInitialLoading || isLoading) return <CouponsTabLoader />;

  return (
    <div className="couponsTab">
      <div className="couponsTabContainer">
        {showDeleteModal && (
          <NudosConfirmActionModal
            firstButtonAction={confirmDeleteCoupon}
            secondButtonAction={() => setShowDeleteModal(false)}
            firstButtonText="Sí, eliminar"
            secondButtonText="No, mantener"
            modalTitle="¿Seguro quieres eliminar este cupón? "
            modalText="Si lo eliminas la empresa ya no podrá visualizarlo en su checkout"
            customHeight="260px"
          />
        )}

        <div className="couponsHeader">
          <div className="sectionTitle">Cupones del cliente</div>
          <div className="sectionDescription">
            Los cupones son por empresa, válidos para un único uso. En caso de querer inactiva un cupón, se debe
            eliminar
          </div>
        </div>

        {!creatingCoupon && (
          <Button
            className="creatButtonWithIcon"
            onClick={() => setCreatingCoupon(true)}
            icon={
              <div className="iconStyle">
                <PlusButton className="iconPositionButton" />
              </div>
            }
          >
            Crear cupón
          </Button>
        )}

        {creatingCoupon && (
          <div className="couponCreationForm">
            <div className="couponFormHeader">
              <NudosTextInput
                label="Valor del cupón"
                placeholder="USD"
                isFilled={!!newCouponValue}
                handleChange={value => setNewCouponValue(Number(value))}
                defaultValue={newCouponValue?.toString()}
                componentSize="small"
                isNumericInput
              />
              <NudosButton
                buttonText="Guardar"
                colorPalette="primary"
                customClassName="createCouponButton"
                handleClick={handleCreateOrganizationCoupon}
                isButtonDisabled={!newCouponValue || isLoading}
                isButtonLoading={isLoading}
              />
            </div>
            <NudosStaticAlertInComponent
              type="information"
              alertOptions={{
                subtitle: 'Todos los cupones creados los podrá visualizar la empresa en el checkout'
              }}
            />
            <div className="cancelCreationLink" onClick={cancelCouponCreation}>
              Cancelar creación
            </div>
          </div>
        )}

        {!creatingCoupon && <CouponsList coupons={coupons} handleDelete={handleDeleteCoupon} />}

        {!creatingCoupon && !!usedCoupons?.length && <div className="dividerLine" />}

        {!creatingCoupon && <UsedCoupons usedCoupons={usedCoupons} />}
      </div>
    </div>
  );
};

export default CouponsTab;
